import { memo, useEffect } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { Box, makeStyles, Typography, Button } from '@material-ui/core'
import Icon from '@material-ui/icons/ErrorOutline'

import { SpacedGroup } from 'UI/Components'

import { logToAnalytics } from 'modules'
import { useRoles, useServiceUsage, useWindowView, useStorage, useBoolean } from 'hooks'
import { showPlanDialog } from 'ducks'

import clsx from 'clsx'
import { paths } from 'app/paths'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: '10px',
        padding: '5px 10px'
    },
    yellow: {
        color: '#f57c00',
        background: 'rgba(245,124,0, 0.06)'
    },
    red: {
        color: '#cb0000',
        background: 'rgba(203,0,0, 0.06)'
    },
    text: {
        [theme.breakpoints.down('md')]: {
            fontSize: '13px'
        }
    },
    btn: {
        whiteSpace: 'nowrap'
    },
    planBtn: {
        boxShadow: 'none',
        [theme.breakpoints.down('md')]: {
            marginLeft: theme.spacing(3)
        }
    },
    yellowBtn: {
        backgroundColor: '#f57c00',
        '&:hover': {
            backgroundColor: '#f57c00'
        },
        '&:active': {
            backgroundColor: '#f57c00'
        }
    },
    additional: {
        marginBottom: '20px'
    }
}))

const specialPaths = [
    paths.editPlaylist,
    paths.editPlaybook,
    paths.playlistDetails,
    paths.playbookDetails,
    paths.spaceLibrary,
    paths.editQuickGuidde,
    paths.spaceDetails
]

const usageBannerBreakpoints = [75, 85, 95, 100]

export const LimitNotification = memo(() => {
    const { isAdmin } = useRoles()
    const { isMobileView } = useWindowView()
    const { isLoading, usedPercent, activePlanId } = useServiceUsage()
    const dispatch = useDispatch()
    const usageLimitExceeded = useBoolean()
    const [notificationValue, setNotificationValue] = useStorage(
        'gd-limit-notification-value',
        0,
        'localStorage'
    )
    // check if a user opened the page
    // in which should add an additional margin
    const addBottomMargin = useRouteMatch(specialPaths)?.isExact

    const classes = useStyles(usedPercent)

    const isMax = usedPercent >= 100

    const text = isMobileView
        ? `You've reached ${usedPercent}% of your plan limits.`
        : `You’ve reached ${usedPercent}% of your plan, upgrade your plan to keep enjoying guidde.`

    useEffect(() => {
        if (isLoading) return

        if (usedPercent < usageBannerBreakpoints[0]) {
            usageLimitExceeded.setFalse()
            setNotificationValue(0)
            return
        }

        if (usedPercent < notificationValue) {
            const exceededBreakpoints = usageBannerBreakpoints.filter(n => n > usedPercent)
            const newNextBreakpoint = exceededBreakpoints.length
                ? Math.min(...exceededBreakpoints)
                : 0

            setNotificationValue(newNextBreakpoint)

            return
        }

        usageLimitExceeded.setTrue()
    }, [
        notificationValue,
        usedPercent,
        setNotificationValue,
        isLoading,
        activePlanId,
        usageLimitExceeded
    ])

    if (isLoading || usageLimitExceeded.isFalse) {
        return null
    }

    return (
        <Box
            mx={3}
            mt={2}
            className={clsx(classes.wrapper, {
                [classes.red]: isMax,
                [classes.yellow]: !isMax,
                [classes.additional]: addBottomMargin
            })}
        >
            <SpacedGroup spacing={1.5}>
                <Icon />

                {usedPercent < 100 ? (
                    <Box>
                        <Typography className={classes.text}>{text}</Typography>
                        {!isAdmin && <Typography>Contact your Admin.</Typography>}
                    </Box>
                ) : (
                    <Typography>
                        You’ve reached your plan limits, upgrade your plan to keep enjoying guidde
                    </Typography>
                )}
            </SpacedGroup>

            <SpacedGroup spacing={1} ml={2}>
                {isAdmin && (
                    <Button
                        size="small"
                        className={clsx(classes.planBtn, classes.btn, {
                            [classes.yellowBtn]: !isMax
                        })}
                        onClick={() => {
                            logToAnalytics('open_plans_dialog_from_limit_notification_bar')
                            dispatch(showPlanDialog('upgradePlanDialog'))
                        }}
                    >
                        View Plans
                    </Button>
                )}
                {usedPercent < 100 && (
                    <Button
                        size="small"
                        color="default"
                        variant="outlined"
                        className={classes.btn}
                        onClick={() => {
                            logToAnalytics('hide_limit_notification_bar', {
                                'used-plan-percent': usedPercent
                            })
                            usageLimitExceeded.setFalse()

                            const exceededBreakpoints = usageBannerBreakpoints.filter(
                                n => n > usedPercent
                            )
                            const newNextBreakpoint = exceededBreakpoints.length
                                ? Math.min(...exceededBreakpoints)
                                : 0

                            setNotificationValue(newNextBreakpoint)
                        }}
                    >
                        Remind me later
                    </Button>
                )}
            </SpacedGroup>
        </Box>
    )
})
